import { useEffect, useRef, useState } from 'react';

import type React from 'react';
import type { Nullish } from '~/types/generics';

type IProps = {
  ref: React.MutableRefObject<Nullish<HTMLElement>> | React.RefObject<Nullish<HTMLElement>>;
  threshold?: number;
  debounceTimeout?: number;
  onScrolledToBottom?: () => void;
};

const useScrollPosition = ({ ref, threshold = 100, debounceTimeout = 300, onScrolledToBottom }: IProps) => {
  const [isScrolledToBottom, setScrolledToBottom] = useState(false);
  const [isScrolledToTop, setScrolledToTop] = useState(true);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) {
      return;
    }

    if (container.scrollHeight <= container.clientHeight + threshold) {
      setScrolledToBottom(true);
    }

    const handleScroll = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setScrolledToTop(container.scrollTop === 0);

        if (container.scrollHeight - container.scrollTop <= container.clientHeight + threshold) {
          onScrolledToBottom?.();
          setScrolledToBottom(true);
        } else {
          setScrolledToBottom(false);
        }
      }, debounceTimeout);
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [onScrolledToBottom, threshold, debounceTimeout, ref]);

  return {
    isScrolledToBottom,
    isScrolledToTop,
  };
};

export default useScrollPosition;
